import { registerPlugin, Capacitor } from '@capacitor/core';

/**
 * Capacitor plugin wrapping native app/website blockers used by the
 * "No Spend Weekend" feature.
 *
 * - iOS: uses Screen Time (FamilyControls + ManagedSettings) to block
 *   user-selected apps and web domains.
 * - Android: uses a foreground AccessibilityService + SYSTEM_ALERT_WINDOW
 *   overlay to intercept foreground app launches matching the user's
 *   blocklist and to block browser navigations to blocked domains.
 *
 * On web (unsupported), methods resolve to no-op / "unavailable" values so
 * the settings UI can render without crashing.
 */

export type BlockingAuthorizationStatus =
  | 'notDetermined'
  | 'denied'
  | 'approved'
  | 'unavailable';

export interface BlockingPermissionState {
  /** Screen Time / FamilyControls on iOS, or usage-access on Android. */
  primary: BlockingAuthorizationStatus;
  /** Android-only: SYSTEM_ALERT_WINDOW permission required to draw the block overlay. */
  overlay?: BlockingAuthorizationStatus;
  /** Android-only: the AccessibilityService the user must enable in system settings. */
  accessibility?: BlockingAuthorizationStatus;
}

export interface PickedAppsToken {
  /**
   * Opaque token representing a selection of apps made by the user through the
   * system picker (iOS FamilyActivityPicker). Persisted and replayed into
   * `startBlocking()`. The token is only meaningful to the native side.
   */
  token: string;
  /** Human-readable count for display in the UI. */
  applicationCount: number;
  categoryCount: number;
  webDomainCount: number;
}

export interface StartBlockingOptions {
  /**
   * iOS: previously returned `PickedAppsToken.token`. Required to block apps
   * picked through the system picker. Web domains are appended from `webDomains`.
   *
   * Android: ignored. On Android the service blocks anything in
   * `androidPackageNames` / `webDomains`.
   */
  pickedAppsToken?: string;
  /** Android-only: bundle IDs to intercept. */
  androidPackageNames?: string[];
  /** Domains to block inside the default browser(s). Applies on both platforms. */
  webDomains?: string[];
  /** Epoch-milliseconds when the block should auto-expire. */
  endsAt: number;
  /** Short label shown in the overlay / shield (e.g. "Shopping Break"). */
  reason?: string;
  /** Individually-picked app count for display in Live Activity / notification. */
  appCount?: number;
  /** iOS Screen Time category count — displayed separately from apps (a category
   *  covers many apps, so calling it an "app" misleads). */
  categoryCount?: number;
  /** Total site/domain count for display in Live Activity / notification. */
  siteCount?: number;
  /** iOS only: start a Live Activity lock-screen countdown when blocking begins. */
  showLiveActivity?: boolean;
  /** Android only: ongoing notification with session countdown when blocking begins. */
  showOngoingNotification?: boolean;
  /** iOS only: a user-authored motivational line. When present, the Live
   *  Activity shows it instead of the rotating quotes/tips. */
  motivationalText?: string;
  /** Admin-authored rotating lines for this event's Live Activity / ongoing
   *  notification (NoSpendEvent.liveActivityMessages). Native picks one for the
   *  session. Falls back to a built-in seed list when empty. `motivationalText`
   *  takes precedence over these. */
  liveActivityMessages?: string[];
}

export interface BlockingStatus {
  active: boolean;
  endsAt?: number;
  reason?: string;
  applicationCount?: number;
  /** iOS only; sessions started before category plumbing won't report it. */
  categoryCount?: number;
  webDomainCount?: number;
}

export interface InstalledAppInfo {
  packageName: string;
  displayName: string;
}

export interface ShoppingBlockerPlugin {
  getAuthorizationStatus(): Promise<BlockingPermissionState>;
  /**
   * Prompts the user for every permission the current platform needs to start
   * blocking. Resolves with the resulting state (may still be `denied` if the
   * user declined). Safe to call repeatedly.
   */
  requestAuthorization(): Promise<BlockingPermissionState>;
  /**
   * iOS only: opens the system FamilyActivityPicker and returns a token
   * representing the user's selection. Pass the previously-saved token as
   * `currentToken` so the picker re-opens with those apps already selected.
   * On Android / web this rejects with code `unavailable`.
   */
  pickApps(options?: { currentToken?: string }): Promise<PickedAppsToken>;
  /** Android only: returns installed apps from our shopping presets list. */
  getInstalledShoppingApps(options: { packageNames: string[] }): Promise<{ apps: InstalledAppInfo[] }>;
  startBlocking(options: StartBlockingOptions): Promise<BlockingStatus>;
  stopBlocking(): Promise<void>;
  getBlockingStatus(): Promise<BlockingStatus>;
  /** Android only: opens system Accessibility Settings so the user can enable our service. */
  openAccessibilitySettings(): Promise<void>;
  /** Android only: opens the system "Display over other apps" settings page. */
  openOverlaySettings(): Promise<void>;
  /** iOS 16.2+: whether Live Activities are allowed in Settings. */
  areLiveActivitiesEnabled(): Promise<{ enabled: boolean }>;
  /** Android 13+: whether POST_NOTIFICATIONS is granted. */
  areNotificationsEnabled(): Promise<{ enabled: boolean }>;
  /** Android: opens app notification settings. */
  openNotificationSettings(): Promise<void>;
}

const Native = registerPlugin<ShoppingBlockerPlugin>('ShoppingBlocker');

const unavailableStatus: BlockingPermissionState = {
  primary: 'unavailable',
  overlay: 'unavailable',
  accessibility: 'unavailable',
};

/**
 * Web-safe wrapper. On native platforms, forwards to the native plugin; on
 * web, returns harmless stubs so the settings view still renders.
 */
export const ShoppingBlocker: ShoppingBlockerPlugin = {
  async getAuthorizationStatus() {
    if (!Capacitor.isNativePlatform()) return unavailableStatus;
    return Native.getAuthorizationStatus();
  },
  async requestAuthorization() {
    if (!Capacitor.isNativePlatform()) return unavailableStatus;
    return Native.requestAuthorization();
  },
  async pickApps(options) {
    if (Capacitor.getPlatform() !== 'ios') {
      throw Object.assign(new Error('App picker is only available on iOS'), { code: 'unavailable' });
    }
    return Native.pickApps(options);
  },
  async getInstalledShoppingApps(options) {
    if (Capacitor.getPlatform() !== 'android') return { apps: [] };
    return Native.getInstalledShoppingApps(options);
  },
  async startBlocking(options) {
    if (!Capacitor.isNativePlatform()) {
      return { active: false };
    }
    return Native.startBlocking(options);
  },
  async stopBlocking() {
    if (!Capacitor.isNativePlatform()) return;
    return Native.stopBlocking();
  },
  async getBlockingStatus() {
    if (!Capacitor.isNativePlatform()) return { active: false };
    return Native.getBlockingStatus();
  },
  async openAccessibilitySettings() {
    if (Capacitor.getPlatform() !== 'android') return;
    return Native.openAccessibilitySettings();
  },
  async openOverlaySettings() {
    if (Capacitor.getPlatform() !== 'android') return;
    return Native.openOverlaySettings();
  },
  async areLiveActivitiesEnabled() {
    if (Capacitor.getPlatform() !== 'ios') return { enabled: false };
    return Native.areLiveActivitiesEnabled();
  },
  async areNotificationsEnabled() {
    if (Capacitor.getPlatform() !== 'android') return { enabled: false };
    return Native.areNotificationsEnabled();
  },
  async openNotificationSettings() {
    if (Capacitor.getPlatform() !== 'android') return;
    return Native.openNotificationSettings();
  },
};

export default ShoppingBlocker;
